import { useQuery } from "@tanstack/react-query";
import { getSupabaseWithLogging } from "@/lib/hooks/api/utils";
import { Task } from "@/lib/hooks/api/tasks";
import { isCalendarDateTodayOrFuture } from "@west-tennessee-consulting/business-date";
import { isCalendarDateOverdue } from "@west-tennessee-consulting/business-date";
import {
  filterArray,
  filterCondition,
  requireOrFilter,
} from "@west-tennessee-consulting/supabase/postgrest-filter";

/**
 * USER TASKS API HOOKS
 * ============================================================================
 *
 * PURPOSE: Specialized hooks for fetching user-specific task data
 *
 * These hooks are designed specifically for dashboard components and provide
 * optimized queries for user-specific task information.
 *
 * ============================================================================
 */

/**
 * Hook to fetch pending tasks assigned to the current user
 *
 * PENDING TASKS DEFINITION:
 * - Tasks with status: 'pending' or 'in_progress'
 * - Assigned to current user (either assigned_to or in assigned_users array)
 * - Ordered by priority (urgent > high > medium > low), then due_date, then created_at
 *
 * @param userId - The ID of the user to fetch tasks for
 * @param limit - Maximum number of tasks to return (default: 5)
 * @param includeOverdue - Whether to include overdue tasks (default: true)
 */
export function useUserPendingTasks(
  userId: string,
  limit: number = 5,
  includeOverdue: boolean = true
) {
  return useQuery({
    queryKey: ["user-pending-tasks", userId, limit, includeOverdue],
    queryFn: async () => {
      const supabase = getSupabaseWithLogging();

      // Build the query for tasks assigned to the user
      let query = supabase.from("contact_tasks").select(`
        *,
        contacts:contact_id (
          id,
          name,
          company_name,
          email
        )
      `);

      // Filter for tasks assigned to the user (by user_id or in assigned_users array)
      query = query.or(
        requireOrFilter([
          filterCondition("user_id", "eq", userId),
          filterArray("assigned_users", "cs", [userId]),
        ])
      );

      // Only get pending and in-progress tasks
      query = query.in("status", ["pending", "in_progress"]);

      // Apply limit
      query = query.limit(limit);

      // Order by priority (urgent > high > medium > low), then due_date, then created_at
      // Note: We'll handle priority ordering in the application layer since Supabase
      // doesn't have a direct way to order by enum values
      query = query.order("due_date", { ascending: true, nullsFirst: false });
      query = query.order("created_at", { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching user pending tasks:", error);
        throw new Error(error.message);
      }

      // Sort by priority in application layer
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      const sortedTasks = (data || []).sort((a, b) => {
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;

        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Higher priority first
        }

        // If same priority, sort by due date
        if (a.due_date && b.due_date) {
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        }

        // If one has due date and other doesn't, prioritize the one with due date
        if (a.due_date && !b.due_date) return -1;
        if (!a.due_date && b.due_date) return 1;

        // Finally, sort by creation date (newest first)
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      // Filter out overdue tasks if requested
      if (!includeOverdue) {
        // A `date` column compared against an instant marks a task due TODAY as
        // past — `new Date("2026-06-16")` is midnight UTC, i.e. the 15th here.
        return sortedTasks.filter(
          (task) => !task.due_date || isCalendarDateTodayOrFuture(task.due_date),
        );
      }

      return sortedTasks;
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to get task statistics for the current user
 *
 * @param userId - The ID of the user to get statistics for
 */
export function useUserTaskStats(userId: string) {
  return useQuery({
    queryKey: ["user-task-stats", userId],
    queryFn: async () => {
      const supabase = getSupabaseWithLogging();

      // Get all tasks assigned to the user
      const { data: allTasks, error } = await supabase
        .from("contact_tasks")
        .select("id, status, priority, due_date, created_at")
        .or(
          requireOrFilter([
            filterCondition("assigned_to", "eq", userId),
            filterArray("assigned_users", "cs", [userId]),
          ])
        );

      if (error) {
        console.error("Error fetching user task stats:", error);
        throw new Error(error.message);
      }

      const tasks = allTasks || [];
      const now = new Date();

      // Calculate statistics
      const stats = {
        total: tasks.length,
        pending: tasks.filter((t) => t.status === "pending").length,
        inProgress: tasks.filter((t) => t.status === "in_progress").length,
        completed: tasks.filter((t) => t.status === "completed").length,
        overdue: tasks.filter((t) => {
          if (!t.due_date || t.status === "completed") return false;
          // A `date` column is a calendar day, not an instant — see @wtc/business-date.
          return isCalendarDateOverdue(t.due_date);
        }).length,
        urgent: tasks.filter((t) => t.priority === "urgent" && t.status !== "completed").length,
        high: tasks.filter((t) => t.priority === "high" && t.status !== "completed").length,
      };

      return stats;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get recent task activity for the current user
 *
 * @param userId - The ID of the user to get activity for
 * @param limit - Maximum number of activities to return (default: 10)
 */
export function useUserTaskActivity(userId: string, limit: number = 10) {
  return useQuery({
    queryKey: ["user-task-activity", userId, limit],
    queryFn: async () => {
      const supabase = getSupabaseWithLogging();

      // Get recent tasks assigned to the user
      const { data: tasks, error } = await supabase
        .from("contact_tasks")
        .select(
          `
          id,
          title,
          status,
          priority,
          updated_at,
          created_at,
          contacts:contact_id (
            id,
            name,
            company_name
          )
        `
        )
        .or(
          requireOrFilter([
            filterCondition("assigned_to", "eq", userId),
            filterArray("assigned_users", "cs", [userId]),
          ])
        )
        .order("updated_at", { ascending: false })
        .limit(limit);

      if (error) {
        console.error("Error fetching user task activity:", error);
        throw new Error(error.message);
      }

      return tasks || [];
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
